import {
  useMemo,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactElement,
} from 'react';
import { CalendarDays, GripVertical, Pencil, Plus } from 'lucide-react';
import {
  type BoardFaceList,
  type BoardMember,
  type BoardTable,
  type BoardTableCustomFieldRow,
  type BoardTableRow,
  type CustomFieldDefinition,
  type CustomFieldValue,
  type Label,
} from '@molt/shared';
import {
  AvatarStack,
  Button,
  IconButton,
  LabelChip,
  TextField,
  profileName,
} from '@/components';
import {
  nextTableSort,
  sortedBoardTableRows,
  type BoardTableSort,
  type BoardTableSortKey,
} from '@/lib/board-table';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { boardViewLabel } from '@/lib/view-state';

/**
 * ⚠ THIS FILE DRAWS A COLUMN PER CUSTOM FIELD, AND THAT REVERSES WHAT THIS
 * HEADER USED TO SAY. The previous text ruled the columns OUT and quoted
 * `FR-VIEW-012`'s edge case as its authority — "card description, custom
 * fields, checklists and attachments are not columns". That sentence no longer
 * exists. The 2026-09-06 corpus amendment rewrote `FR-VIEW-012`'s behaviour to
 * name "an optional column for each custom field defined on that board
 * (`FR-CARD-027`)", replaced the edge case with a Settled one that says custom
 * fields "*are* available as columns here", and left `FR-VIEW-016`'s refusal
 * standing on the WORKSPACE table for a stated reason. The old ruling was a
 * correct reading of the old corpus; it is quoted here only so nobody restores
 * it from the git history believing it was overturned by taste.
 *
 * ⚠ THE ASYMMETRY IS THE REQUIREMENT, not a compromise. Both Settled edge cases
 * give the same reason: field definitions are BOARD-SCOPED, so this view has
 * exactly one well-defined field set to build columns from, and a Workspace
 * Table spanning up to 20 boards has none. `WorkspaceTablePage.tsx` must
 * therefore go on drawing its six fixed columns — and because Zod strips
 * unknown keys, no server-side assertion can prove it does. The pin that
 * discriminates is `WorkspaceTablePage.test.tsx`'s header-set equality, handed a
 * row that DOES carry the key.
 *
 * ⚠ HIDDEN DEFINITIONS GET NO COLUMN. `FR-SEARCH-031` refuses them a filter
 * facet "because they present nowhere else either", and a column here would
 * make that shipped sentence false. The reasoning in full, including the
 * `FR-CARD-027` sentence it has to be reconciled with, is on
 * `readTableCustomFieldValues` in `apps/server/src/custom-fields/read.ts`. It is
 * enforced on BOTH sides deliberately: the server projects no hidden VALUE, and
 * the filter below draws no hidden COLUMN. Neither alone is enough — a hidden
 * field that happens to hold no values would still get an empty column from the
 * server side alone.
 *
 * ⚠ THE COLUMNS ARE NOT IN `table`. They come from the board's definitions
 * (`boardCustomFieldsQueryOptions`, `GET /boards/:id/custom-fields`), which is
 * the ONE projection of them; the table payload carries only the per-card
 * VALUES, each already keyed by `definitionId`. Two consequences follow and both
 * are deliberate. A definition with no value on any card still gets a column of
 * empty cells, which is exactly `FR-VIEW-012`'s "no data precondition". And the
 * column ORDER is the definitions' array order — never re-sorted here, because
 * `readBoardCustomFieldDefinitions` already orders by the lexorank `position`
 * column, whose `COLLATE "C"` ordering a JavaScript `localeCompare` would
 * silently disagree with.
 *
 * ⚠ AND THERE IS NO COLUMN CHOOSER, which is what "optional" in the behaviour
 * sentence does NOT mean. `04-views.md`'s own open questions record that
 * Trello's table has "no group-by control and no column chooser" and that
 * "a clone adding table grouping or configurable columns is exceeding parity".
 * Optional means the column exists when the definition does.
 *
 * ⚠ THE CELLS ARE READ-ONLY, and that is `FR-VIEW-013` rather than an omission:
 * it names "a cell for list, labels, members or due date" as what opens an
 * editor, and custom fields are not on that list. The card back is the route to
 * changing a value.
 *
 * ⚠ WHAT DID NOT CHANGE, kept because it reads as a bug and is not.
 * `FR-SEARCH-007`'s keyword facet matches a card by its title OR by any one of
 * its custom-field values (`apps/server/src/boards/filter.ts`,
 * `matchesKeywordFacet`), and that match ignores `hidden` — hidden is
 * presentation, not access control. So while a keyword filter is active this
 * table may still hold a row none of whose visible cells contains any of the
 * search terms: it matched on a HIDDEN field's value, which has no column here
 * by the ruling above. MEMBERSHIP IS THE SERVER'S DECISION. This component must
 * never re-filter `table.rows` client-side — it cannot, being given
 * `filterActive: boolean` and never the keyword itself — and
 * `boardTable.summary` must go on counting the rows it was handed rather than
 * any board-wide total. Both halves are asserted in `BoardTableView.test.tsx`;
 * a client-side title filter added later is the defect that would make those
 * matches vanish with nothing red anywhere.
 */

export interface BoardTableMoveIntent {
  readonly cardId: string;
  readonly fromListId: string;
  readonly toListId: string;
  readonly beforeCardId: string | null;
}

export interface BoardTableCreateIntent {
  readonly title: string;
  readonly listId: string;
}

export interface BoardTableViewProps {
  readonly table: BoardTable;
  readonly allLists: readonly BoardFaceList[];
  readonly allLabels: readonly Label[];
  readonly allMembers: readonly BoardMember[];
  /**
   * The board's custom field definitions, in their own order — the columns.
   * Defaults to none so every caller that has not wired
   * `boardCustomFieldsQueryOptions` renders exactly the six fixed columns.
   */
  readonly customFields?: readonly CustomFieldDefinition[] | undefined;
  readonly readonly?: boolean | undefined;
  readonly filterActive?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly onOpenCard?: ((row: BoardTableRow) => void) | undefined;
  readonly onRenameCard?: ((cardId: string, title: string) => void) | undefined;
  readonly onMoveCard?: ((intent: BoardTableMoveIntent) => void) | undefined;
  readonly onSetDueDate?: ((cardId: string, dueAt: string | null) => void) | undefined;
  readonly onAddLabel?: ((cardId: string, labelId: string) => void) | undefined;
  readonly onRemoveLabel?: ((cardId: string, labelId: string) => void) | undefined;
  readonly onAddMember?: ((cardId: string, userId: string) => void) | undefined;
  readonly onRemoveMember?: ((cardId: string, userId: string) => void) | undefined;
  readonly onCreateCard?: ((intent: BoardTableCreateIntent) => void) | undefined;
}

/**
 * ⚠ A KEY, not a translated sentence, and the difference is not cosmetic.
 * `dragRefusalKey` below is state that OUTLIVES the render which set it, so a
 * resolved sentence stored in it would still be on screen in the previous
 * language after the catalogue query resolves underneath — the defect
 * `BoardMapView.tsx`'s `localErrorKey` header records and that no test in this
 * repository can see. Everything here holds keys; `t` is applied at render.
 */
const LOCKED_REASON_KEY = 'boardTable.lockedReason';

export function BoardTableView({
  table,
  allLists,
  allLabels,
  allMembers,
  customFields = [],
  readonly = false,
  filterActive = false,
  busy = false,
  onOpenCard,
  onRenameCard,
  onMoveCard,
  onSetDueDate,
  onAddLabel,
  onRemoveLabel,
  onAddMember,
  onRemoveMember,
  onCreateCard,
}: BoardTableViewProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [sort, setSort] = useState<BoardTableSort | null>(null);
  const [renameCardId, setRenameCardId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [dragRefusalKey, setDragRefusalKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createListId, setCreateListId] = useState(allLists[0]?.id ?? '');
  const rows = useMemo(() => sortedBoardTableRows(table.rows, sort), [sort, table.rows]);
  /* ⚠ `filter`, never `sort`. See the header: the incoming order is the
     lexorank `position` order the server already applied under `COLLATE "C"`,
     and re-sorting it in JavaScript is how that ordering gets silently
     inverted. */
  const fieldColumns = useMemo(
    () => customFields.filter((definition) => definition.hidden === false),
    [customFields],
  );
  const canEdit = !readonly && table.board.closed === false;
  const dragDisabled = sort !== null || filterActive || !canEdit;
  const dragDisabledKey: string | null =
    sort !== null
      ? 'boardTable.dragDisabled.sort'
      : filterActive
        ? 'boardTable.dragDisabled.filter'
        : !canEdit
          ? LOCKED_REASON_KEY
          : null;
  /* Hoisted so the render below narrows: `a ?? b` is null exactly when both
     are, which the two-sided emptiness test the JSX used to spell could not
     tell `tsc`. */
  const refusalKey = dragRefusalKey ?? dragDisabledKey;

  function changeSort(key: BoardTableSortKey): void {
    setSort((current) => nextTableSort(current, key));
    setDragRefusalKey(null);
  }

  function submitRename(event: FormEvent<HTMLFormElement>, row: BoardTableRow): void {
    event.preventDefault();
    const title = renameDraft.trim();
    if (title !== '' && title !== row.card.title) onRenameCard?.(row.card.id, title);
    setRenameCardId(null);
    setRenameDraft('');
  }

  function submitCreate(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const title = createTitle.trim();
    if (title === '' || createListId === '') return;
    onCreateCard?.({ title, listId: createListId });
    setCreateTitle('');
    setCreateOpen(false);
  }

  function handleDragStart(
    event: DragEvent<HTMLTableRowElement>,
    row: BoardTableRow,
  ): void {
    if (dragDisabled) {
      event.preventDefault();
      setDragRefusalKey(dragDisabledKey);
      return;
    }
    setDraggedCardId(row.card.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', row.card.id);
  }

  function handleDrop(event: DragEvent<HTMLTableRowElement>, row: BoardTableRow): void {
    event.preventDefault();
    const cardId = event.dataTransfer.getData('text/plain') || draggedCardId;
    setDraggedCardId(null);
    if (cardId === null || cardId === '' || cardId === row.card.id) return;
    if (dragDisabled) {
      setDragRefusalKey(dragDisabledKey);
      return;
    }
    onMoveCard?.({
      cardId,
      fromListId: '',
      toListId: row.list.id,
      beforeCardId: row.card.id,
    });
  }

  return (
    <section
      id="board-view-content"
      /* ⚠ REUSED, not declared. `BoardFace.tsx` names this same
         `id="board-view-content"` region with this same composition in its
         error and loading branches; a literal here made the loaded branch a
         second author of one accessible name. */
      aria-label={t('boardFace.viewRegion', { view: boardViewLabel(t, 'table') })}
      className="flex min-h-0 flex-1 flex-col gap-3 bg-surface px-3 pb-3 text-ink"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-ink">{t('boardTable.heading')}</h2>
          <p className="text-card text-ink-muted">
            {t(
              'boardTable.summary',
              { count: rows.length, board: table.board.title },
              rows.length,
            )}
          </p>
        </div>
        {canEdit ? (
          <Button
            type="button"
            size="sm"
            variant={createOpen ? 'toggle' : 'secondary'}
            pressed={createOpen}
            iconStart={<Plus aria-hidden="true" className="size-4" />}
            onClick={() => {
              setCreateOpen((open) => !open);
              setCreateListId((current) =>
                current !== '' ? current : (allLists[0]?.id ?? ''),
              );
            }}
          >
            {t('boardTable.addCard')}
          </Button>
        ) : null}
      </div>

      {refusalKey === null ? null : (
        <p role="status" className="text-card text-ink-muted">
          {t(refusalKey)}
        </p>
      )}

      {createOpen ? (
        <form
          aria-label={t('boardTable.createFormLabel')}
          className="flex flex-wrap items-end gap-2 rounded-card border border-border-strong bg-panel p-3"
          onSubmit={submitCreate}
        >
          <TextField
            label={t('boardTable.cardTitle')}
            value={createTitle}
            onChange={(event) => setCreateTitle(event.currentTarget.value)}
          />
          <label className="flex min-w-48 flex-col gap-1 text-card font-medium text-ink">
            {t('boardTable.listLabel')}
            <select
              className="h-9 rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
              value={createListId}
              onChange={(event) => setCreateListId(event.currentTarget.value)}
            >
              {allLists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.title}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            busy={busy}
            disabled={createTitle.trim() === ''}
          >
            {t('boardTable.create')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="quiet"
            onClick={() => {
              setCreateOpen(false);
              setCreateTitle('');
            }}
          >
            {t('boardTable.cancel')}
          </Button>
        </form>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto rounded-card border border-border-strong bg-panel">
        <table className="min-w-[58rem] w-full border-collapse text-left text-card">
          <thead className="sticky top-0 z-10 bg-surface-raised text-xs uppercase text-ink-muted">
            <tr>
              <th className="w-9 px-2 py-2">{t('boardTable.column.move')}</th>
              <SortableHeading
                label={t('boardTable.column.card')}
                sortKey="card"
                sort={sort}
                onSort={changeSort}
              />
              <SortableHeading
                label={t('boardTable.column.list')}
                sortKey="list"
                sort={sort}
                onSort={changeSort}
              />
              <th className="px-2 py-2">{t('boardTable.column.labels')}</th>
              <th className="px-2 py-2">{t('boardTable.column.members')}</th>
              <SortableHeading
                label={t('boardTable.column.dueDate')}
                sortKey="due"
                sort={sort}
                onSort={changeSort}
              />
              {/* FR-VIEW-012 — one column per non-hidden definition, in the
                  definitions' own order. The header is the field NAME, which is
                  user data rather than copy and so carries no catalogue key.
                  NOT a SortableHeading: FR-VIEW-014's sort keys are card, list
                  and due date, and `BoardTableSortKey` has no arm for a field. */}
              {fieldColumns.map((definition) => (
                <th key={definition.id} scope="col" className="min-w-40 px-2 py-2">
                  {definition.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.card.id}
                draggable={!dragDisabled}
                onDragStart={(event) => handleDragStart(event, row)}
                onDragOver={(event) => {
                  if (!dragDisabled) event.preventDefault();
                }}
                onDrop={(event) => handleDrop(event, row)}
                className={cn(
                  'border-t border-border align-top',
                  draggedCardId === row.card.id && 'opacity-50',
                )}
              >
                <td className="px-2 py-2 text-ink-muted">
                  <GripVertical aria-hidden="true" className="size-4" />
                </td>
                <td className="min-w-64 px-2 py-2">
                  {renameCardId === row.card.id ? (
                    <form
                      className="flex items-center gap-1"
                      onSubmit={(event) => submitRename(event, row)}
                    >
                      <TextField
                        label={t('boardTable.renameCard', { title: row.card.title })}
                        value={renameDraft}
                        onChange={(event) => setRenameDraft(event.currentTarget.value)}
                      />
                      <Button type="submit" size="sm" variant="primary" busy={busy}>
                        {t('boardTable.save')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="quiet"
                        onClick={() => setRenameCardId(null)}
                      >
                        {t('boardTable.renameCancel')}
                      </Button>
                    </form>
                  ) : (
                    <div className="flex min-w-0 items-center gap-1">
                      <button
                        type="button"
                        className="min-w-0 truncate text-left font-medium text-ink underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        onClick={() => onOpenCard?.(row)}
                      >
                        {row.card.title}
                      </button>
                      {canEdit ? (
                        <IconButton
                          label={t('boardTable.renameCard', { title: row.card.title })}
                          size="sm"
                          onClick={() => {
                            setRenameCardId(row.card.id);
                            setRenameDraft(row.card.title);
                          }}
                        >
                          <Pencil aria-hidden="true" className="size-3.5" />
                        </IconButton>
                      ) : null}
                    </div>
                  )}
                </td>
                <td className="min-w-40 px-2 py-2">
                  {canEdit ? (
                    <select
                      aria-label={t('boardTable.changeList', { title: row.card.title })}
                      className="h-8 w-full rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
                      value={row.list.id}
                      onChange={(event) =>
                        onMoveCard?.({
                          cardId: row.card.id,
                          fromListId: row.list.id,
                          toListId: event.currentTarget.value,
                          beforeCardId: null,
                        })
                      }
                    >
                      {allLists.map((list) => (
                        <option key={list.id} value={list.id}>
                          {list.title}
                        </option>
                      ))}
                    </select>
                  ) : (
                    row.list.title
                  )}
                </td>
                <td className="min-w-52 px-2 py-2">
                  {canEdit ? (
                    <FacetEditor
                      title={t('boardTable.editLabels', { title: row.card.title })}
                    >
                      {allLabels.map((label) => {
                        const checked = row.labels.some((item) => item.id === label.id);
                        return (
                          <label key={label.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                checked
                                  ? onRemoveLabel?.(row.card.id, label.id)
                                  : onAddLabel?.(row.card.id, label.id)
                              }
                            />
                            <LabelChip
                              colour={label.colour}
                              name={label.name}
                              size="sm"
                            />
                          </label>
                        );
                      })}
                    </FacetEditor>
                  ) : row.labels.length === 0 ? (
                    <span className="text-ink-muted">{t('boardTable.noLabels')}</span>
                  ) : null}
                  {row.labels.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {row.labels.map((label) => (
                        <LabelChip
                          key={label.id}
                          colour={label.colour}
                          name={label.name}
                          size="sm"
                        />
                      ))}
                    </div>
                  ) : canEdit ? (
                    <span className="mt-1 block text-ink-muted">
                      {t('boardTable.noLabels')}
                    </span>
                  ) : null}
                </td>
                <td className="min-w-48 px-2 py-2">
                  {canEdit ? (
                    <FacetEditor
                      title={t('boardTable.editMembers', { title: row.card.title })}
                    >
                      {allMembers.map((member) => {
                        const checked = row.members.some((item) => item.id === member.id);
                        return (
                          <label key={member.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                checked
                                  ? onRemoveMember?.(row.card.id, member.id)
                                  : onAddMember?.(row.card.id, member.id)
                              }
                            />
                            <span>{profileName(member)}</span>
                          </label>
                        );
                      })}
                    </FacetEditor>
                  ) : row.members.length === 0 ? (
                    <span className="text-ink-muted">{t('boardTable.noMembers')}</span>
                  ) : null}
                  {row.members.length > 0 ? (
                    <AvatarStack
                      members={row.members}
                      size="xs"
                      max={5}
                      overflowAction="none"
                      names="tooltip"
                    />
                  ) : canEdit ? (
                    <span className="text-ink-muted">{t('boardTable.noMembers')}</span>
                  ) : null}
                </td>
                <td className="min-w-44 px-2 py-2">
                  {canEdit ? (
                    <div className="flex flex-col gap-1">
                      <label className="flex items-center gap-2">
                        <CalendarDays
                          aria-hidden="true"
                          className="size-4 text-ink-muted"
                        />
                        <input
                          aria-label={t('boardTable.setDueDate', {
                            title: row.card.title,
                          })}
                          type="date"
                          value={dateInputValue(row.card.dueAt)}
                          className="h-8 rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
                          onChange={(event) =>
                            onSetDueDate?.(
                              row.card.id,
                              event.currentTarget.value === ''
                                ? null
                                : `${event.currentTarget.value}T17:00:00.000Z`,
                            )
                          }
                        />
                      </label>
                      {row.card.dueAt === null ? (
                        <span className="text-ink-muted">
                          {t('boardTable.noDueDate')}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    dueDateLabel(t, row.card.dueAt)
                  )}
                </td>
                {/* ⚠ A cell for EVERY column, present whether or not the card
                    carries a value: FR-VIEW-012 has no data precondition, so a
                    card with no custom field values is a full row with empty
                    cells. Read-only by FR-VIEW-013, which names list, labels,
                    members and due date as the editable cells and not these. */}
                {fieldColumns.map((definition) => (
                  <td key={definition.id} className="min-w-40 px-2 py-2 text-ink-muted">
                    {customFieldCellText(
                      t,
                      definition,
                      findCustomFieldValue(row, definition.id),
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface SortableHeadingProps {
  readonly label: string;
  readonly sortKey: BoardTableSortKey;
  readonly sort: BoardTableSort | null;
  readonly onSort: (key: BoardTableSortKey) => void;
}

function SortableHeading({
  label,
  sortKey,
  sort,
  onSort,
}: SortableHeadingProps): ReactElement {
  const active = sort?.key === sortKey;
  return (
    <th
      scope="col"
      aria-sort={
        active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      className="px-2 py-2"
    >
      <button
        type="button"
        className="font-semibold hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        onClick={() => onSort(sortKey)}
      >
        {label}
        {active ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );
}

function FacetEditor({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactElement | readonly ReactElement[];
}): ReactElement {
  return (
    <details className="group">
      <summary className="cursor-pointer text-sm font-medium text-ink underline-offset-2 hover:underline">
        {title}
      </summary>
      <div className="mt-2 flex max-h-48 min-w-48 flex-col gap-2 overflow-y-auto rounded-card border border-border bg-surface p-2">
        {children}
      </div>
    </details>
  );
}

function dateInputValue(value: string | null): string {
  if (value === null) return '';
  return value.slice(0, 10);
}

/**
 * ⚠ `t` FIRST and REQUIRED, the shape `BoardMapView.tsx`'s module-level helpers
 * and `cardDisplayTitle` settled: a defaulted translator is what lets one
 * caller fall through to a default that looks correct.
 *
 * ⚠ The OTHER return is NOT routed and is reported rather than fixed.
 * `new Intl.DateTimeFormat(undefined, …)` means "the BROWSER's locale", which
 * ignores the user's own locale preferences entirely, so a Spanish interface
 * can still draw an English-formatted date. `lib/locale-formatting.ts` already
 * holds the fix (`formatLocaleCardDate`, `effectiveTimezone`); wiring it needs
 * locale-preferences plumbing into a module-level helper, which is locale work
 * rather than catalogue routing and would widen this slice past the file. It is
 * the same class as the named `calendarDateLabel` residual.
 */
function dueDateLabel(
  t: InterfaceLanguageContextValue['t'],
  value: string | null,
): string {
  if (value === null) return t('boardTable.noDueDate');
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
    new Date(value),
  );
}

/**
 * The value this row carries for `definitionId`, or `undefined`.
 *
 * ⚠ `undefined` and not `null`, and the distinction is the whole reason this is
 * a named function. `customFieldValueSchema` has no "empty" arm — a cleared
 * text field, an unticked checkbox and a field the member never opened all
 * arrive as an ABSENT entry — so "no entry" is the only empty this row shape
 * can express, and `customFieldCellText` must decide what that means per type.
 */
function findCustomFieldValue(
  row: BoardTableCustomFieldRow,
  definitionId: string,
): CustomFieldValue | undefined {
  return row.customFields.find((value) => value.definitionId === definitionId);
}

/**
 * ⚠ `t` FIRST and REQUIRED, the shape `dueDateLabel` above settled: a defaulted
 * translator is what lets one caller fall through to a default that looks
 * correct.
 *
 * ⚠ THE CHECKBOX ARM COMES BEFORE THE EMPTY ARM, and swapping them is a real
 * defect rather than a reordering. `toValue` on the server returns `null` for an
 * unticked checkbox, so an unticked box and an untouched one are the same
 * absence in the payload — but the DEFINITION knows the type, and an unticked
 * checkbox has an answer where a blank text field does not. Reporting "No
 * value" for it would state a fact the field does not have.
 *
 * ⚠ The date arm carries the SAME unrouted `Intl.DateTimeFormat(undefined, …)`
 * that `dueDateLabel` above records as a residual: it means "the BROWSER's
 * locale", not the member's. `lib/locale-formatting.ts` holds the fix and
 * wiring it needs locale-preferences plumbing into a module-level helper. Kept
 * identical to its neighbour deliberately, so one change fixes both rather than
 * leaving the table formatting two dates two ways.
 */
function customFieldCellText(
  t: InterfaceLanguageContextValue['t'],
  definition: CustomFieldDefinition,
  value: CustomFieldValue | undefined,
): string {
  if (definition.type === 'checkbox') {
    return value === undefined
      ? t('boardTable.column.customFieldUnticked')
      : t('boardTable.column.customFieldTicked');
  }
  if (value === undefined) return t('boardTable.column.customFieldEmpty');

  switch (value.type) {
    case 'text':
      return value.value;
    case 'number':
      return String(value.value);
    case 'date':
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
        new Date(value.value),
      );
    case 'dropdown':
      return value.label;
    /* Unreachable while the definition and the value agree on type, and kept
       rather than thrown so a retyped field renders something honest instead of
       blanking the row. */
    case 'checkbox':
      return t('boardTable.column.customFieldTicked');
  }
}
